// --------------------------
// IMPORTS
// --------------------------
import { useState, useEffect } from "react";
import { Form, Input, InputNumber } from "antd";
import { useDispatch, useSelector } from "react-redux";
import CustomLoader from "../../utils/CustomLoader";
import TableContent from "../../components/atoms/TableContent";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import useCurrentPath, {
  deFaultHandlePaginationChange,
  formateDateTime,
  handleDelete,
} from "../../utils/Constants";
import media from "../../media";
import {
  addExpenseCodeThunk,
  deleteExpenseCodeThunk,
  editExpenseCodeThunk,
  getExpenseCodeListThunk,
} from "../../store/thunks/expenseCodesThunk";
import EditForm from "../../components/molecules/Forms/editForm";
import { selectedRowInfo } from "../../store/slices/authSlice";

// --------------------------
// REUSABLE CELL COMPONENT
// --------------------------
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function ExpenseCode() {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const [form] = Form.useForm();
  const [tableForm] = Form.useForm();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const expenseCodes = useSelector((state) => state.expenseCodes.list) || [];
  const expenseCodesLoading = useSelector(
    (state) => state.expenseCodes.loading
  );
  const storePageInfo = useSelector((state) => state.expenseCodes.pageInfo);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [storeTotalCount, setStoreTotalCount] = useState(0);  
  const [filteredExpenseCodes, setFilteredExpenseCodes] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingExpenseCode, setEditingExpenseCode] = useState(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [addExpenseCode, setAddExpenseCode] = useState({
    name: "",
    costCode: "",
    history: "",
    status: "",
    createdAt: "",
    updatedAt: "",
  });
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record) => record.key === editingKey;

  // --------------------------
  // API CALLS
  // --------------------------

  // Fetch ExpenseCodes
  const fetchExpenseCodes = async () => {    
    const res = await dispatch(
      getExpenseCodeListThunk({
        route: currentPath,
        pagination: {
          current: 1,
          pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
        },
      })
    ).unwrap();

    setStoreTotalCount(res.totalCount || 0);
    setShowLoader(false);
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    fetchExpenseCodes();
  }, [currentPath, dispatch]);

  useEffect(() => {
    setFilteredExpenseCodes(expenseCodes); // sync on data change
  }, [expenseCodes]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const openEditModal = (record) => {
    setEditingExpenseCode(record); // store the record to edit
    tableForm.setFieldsValue({
      name: record.name,
      costCode: record.costCode,
    });
    setIsEditModalOpen(true); // open the modal
  };

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    {
      title: "Sr. No.",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Expense Name",
      dataIndex: "name",
      editable: true,      
    },
    {
      title: "Expense Code",
      dataIndex: "costCode",

      editable: true,
      sorter: (a, b) => a.costCode.localeCompare(b.costCode),
    },
    {
      title: "Status",
      dataIndex: "status",
      editable: true,
      className: "all-nowrap-column",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (text) => (
        <>
          <span className={`${text?.toLowerCase()}Status`}>{text}</span>
        </>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (text) => text && formateDateTime(text),
    },
    ...(storeRole === process.env.REACT_APP_SROLE ||
    storeRole === process.env.REACT_APP_ADROLE
      ? [
          {
            title: "Updated At",
            dataIndex: "updatedAt",
            render: (text) => text && formateDateTime(text),
          },
          {
            title: "Action",
            dataIndex: "action",
            render: (_, record) => (
              <span
                className="table-round-btn"
                onClick={() => openEditModal(record)}
              >
                <img src={media.editIcon.svg} alt="Edit icon" />
                <span className="text-size-12 dark-black-color">Edit</span>
              </span>
            ),
          },
        ]
      : []),
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      const selectedRowKeysChildInfoArr = selectedRows.map((row) => row.key);

      dispatch(
        selectedRowInfo({
          selectedRowKeysChildInfo: selectedRowKeysChildInfoArr,
        })
      );
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  // --------------------------
  // FORM FIELDS
  // --------------------------
  const formFields = [
    {
      isVisible: true,
      label: "Expense Name",
      name: "name",
      rules: [
        {
          required: true,
          message: "Please enter expense name",
        },
      ],
      placeholder: "Expense Name",
      type: "input",
      onChange: (e) => {
        setAddExpenseCode({
          ...addExpenseCode,
          name: e.target.value,
        });
      },
    },
    {
      isVisible: true,
      label: "Expense Code",
      name: "costCode",
      rules: [
        {
          required: true,
          message: "Please enter expense code",
        },
      ],
      placeholder: "Expense Code",
      type: "input",
      onChange: (e) => {
        setAddExpenseCode({
          ...addExpenseCode,
          costCode: e.target.value,
        });
      },
    },
  ];

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      <div className="bgi-no-repeat card-stretch border-2 rounded-lg p-0">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Expense Code"
          sectionDescription={
            showLoader ? "Loading..." : `${storeTotalCount} Found`
          }
          hasSectionButton={false}
          hasSearch={true}
          hasSearchButton="general"
          addNewButtonClassName="btn mx-auto px-5 py-2 defaultbutton rounded-lg"
          name={storeRole === process.env.REACT_APP_SROLE ? "Add New Code" : ""}
          searchClassName="table-search"
          searchPlaceholder="Search Expense Name & Code"
          onButtonClick={() => setIsAddModalOpen(true)}
          searchData={expenseCodes}
          onFilteredData={setFilteredExpenseCodes}
          searchKeys={["name", "costCode"]}
          addFormModalOpen={isAddModalOpen}
          addFormSetIsModalOpen={setIsAddModalOpen}
          addFormModalShowLoader={setShowLoader}
          payload={(values) => ({
            route: currentPath,
            name: values?.name,
            costCode: values?.costCode,
            historySource: "backend",
            historyAction: "create",
            historyUserID: storeLIUId,
          })}
          list={addExpenseCodeThunk}
          formFields={formFields}
          successMessage="Expense Code added successfully."
          onSuccess={fetchExpenseCodes}
          selectedRowKeys={
            storeRole === process.env.REACT_APP_SROLE && selectedRowKeys
          }
          deleteOnClick={() => {
            handleDelete({
              selectedRowKeys,
              dispatch,
              deleteThunk: deleteExpenseCodeThunk,
              route: currentPath,
              actionName: "expenseCode",
              onSuccess: () => setSelectedRowKeys([]),
            });
          }}
          searchText={searchText}
          setSearchText={(value) => {
            setShowLoader(true);
            setSearchText(value);
            setTimeout(() => setShowLoader(false), 500);
          }}
        />
        <div
          className={`card p-5 rounded-lg paddingTop-0 ${
            storeTotalCount <= process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE
              ? "paddingBottom-0"
              : "minHeight-100vh"
          }`}
        >
          <div className="table-area">
            <div className="table-responsive">
              <div className="tab-content" id="alltable-size">
                <Form form={tableForm} component={false}>
                  <TableContent                    
                    bordered={false}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    rowSelection={
                      storeRole === process.env.REACT_APP_SROLE && rowSelection
                    }
                    storeList={filteredExpenseCodes}
                    components={{
                      body: {
                        cell: EditableCell,
                      },
                    }}
                    pagination={storePageInfo}
                    loading={showLoader}
                    totalCount={storeTotalCount}
                    handlePaginationChange={(page, pageSize) =>
                      deFaultHandlePaginationChange(
                        (type, pagination) =>
                          dispatch(
                            getExpenseCodeListThunk({
                              route: currentPath,
                              pagination,
                            })
                          ),
                        page,
                        pageSize,
                        storeTotalCount
                      )
                    }
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>
        <EditForm
          title="Edit Expense Code"
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          form={tableForm}
          fields={[
            {
              label: "Expense Name",
              name: "name",
              rules: [
                { required: true, message: "Please input expense name!" },
              ],
              type: "input",
            },
            { label: "Expense Code", name: "costCode", type: "input" },
          ]}
          submitThunk={editExpenseCodeThunk}
          payloadBuilder={(values) => ({
            id: editingExpenseCode.key,
            name: values.name,
            costCode: values.costCode,
            historySource: "backend",
            historyAction: "update",
            historyUserID: storeLIUId,
          })}
          successMessage="Expense Code updated successfully."
          currentPath={currentPath}
          refreshListThunk={getExpenseCodeListThunk}
        />
      </div>
    </div>
  );
}
